import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, RefreshCw,
  Loader2, Wifi, Database, Mic, Radio, Cpu, Network, KeyRound, Headphones,
  Send, LifeBuoy, Terminal, Eraser, Download, Mail,
} from "lucide-react";
import { getConsoleTail, clearConsoleTail } from "@/lib/consoleTail";

/* ─────────────────────────────────────────────────────────────────────────
 *  /status — Studio self-test dashboard
 *
 *  Real client-side probes that exercise every primitive the collab stack
 *  depends on. Designed so an end-user can share a screenshot with support
 *  when they report "collab feels laggy" — the dashboard alone makes 80%
 *  of bug reports self-diagnosable.
 *
 *  Each probe is a tiny independent async fn returning:
 *    { ok: boolean, detail?: string, ms?: number }
 *
 *  Probes auto-run on mount and every 10s; Refresh button forces a re-run.
 *  ───────────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, (m) => m === "https:" ? "wss:" : "ws:");
// Optional default destination for the "Email to support" deep-link.
// If unset (or set to empty), the mailto: opens with an empty `to:` field
// so the user can pick a recipient. Set in /app/frontend/.env when you
// want all support emails routed somewhere specific.
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "";

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-50">
      <div className="absolute -left-40 top-0 h-[460px] w-[460px] rounded-full bg-sky-500/[0.18] blur-3xl" />
      <div className="absolute right-[-220px] top-40 h-[540px] w-[540px] rounded-full bg-fuchsia-500/[0.14] blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-500/[0.10] blur-3xl" />
    </div>
  );
}

async function timed(fn) {
  const t0 = performance.now();
  try {
    const v = await fn();
    return { ms: Math.round(performance.now() - t0), ...v };
  } catch (e) {
    return { ms: Math.round(performance.now() - t0), ok: false, detail: e?.message || String(e) };
  }
}

/** Probe — backend health endpoint reachable + Mongo ping ok. */
async function probeBackend() {
  return timed(async () => {
    const r = await axios.get(`${API}/status/health`, { timeout: 4000 });
    if (!r?.data?.ok) return { ok: false, detail: "non-ok response" };
    if (r.data.mongo_ok === false) return { ok: false, detail: "Mongo ping failed on the server" };
    return { ok: true, detail: `Mongo ok · ${r.data.collab?.active_rooms || 0} live rooms · ${r.data.collab?.active_clients || 0} clients`, extra: r.data };
  });
}

/** Probe — WebSocket connectable (we open + immediately close one). */
async function probeWebSocket() {
  return timed(async () => {
    // Use a real collab WS path with a throwaway room id so the connection
    // exercises the same upgrade handshake users go through.
    const rid = "probe-" + Math.random().toString(36).slice(2, 10);
    const url = `${WS_BASE}/api/collab/ws/${rid}?name=Probe`;
    return await new Promise((resolve) => {
      let settled = false;
      const ws = new WebSocket(url);
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        try { ws.close(); } catch (e) { void e; }
        resolve({ ok: false, detail: "5s timeout" });
      }, 5000);
      ws.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { ws.close(); } catch (e) { void e; }
        resolve({ ok: true, detail: "Upgrade + welcome ok" });
      };
      ws.onerror = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ ok: false, detail: "WS error event" });
      };
    });
  });
}

/** Probe — STUN reachable. We create a stub RTCPeerConnection and watch
 *  for ANY ICE candidate. A `srflx` (server-reflexive) candidate confirms
 *  STUN works; a `host`-only result means we can't escape the LAN. */
async function probeSTUN() {
  return timed(async () => {
    if (typeof RTCPeerConnection === "undefined") {
      return { ok: false, detail: "RTCPeerConnection not supported" };
    }
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    // Need at least one data channel to trigger ICE gathering.
    pc.createDataChannel("probe", { ordered: true });
    let gotSrflx = false;
    return await new Promise(async (resolve) => {
      const timer = setTimeout(() => {
        try { pc.close(); } catch (e) { void e; }
        resolve(gotSrflx
          ? { ok: true, detail: "STUN reachable · srflx candidate gathered" }
          : { ok: false, detail: "no srflx candidate within 4s — STUN unreachable or symmetric NAT" });
      }, 4000);
      pc.onicecandidate = (ev) => {
        if (ev.candidate?.candidate?.includes("typ srflx")) {
          gotSrflx = true;
          clearTimeout(timer);
          try { pc.close(); } catch (e) { void e; }
          resolve({ ok: true, detail: "STUN reachable · srflx candidate gathered" });
        }
      };
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
      } catch (e) {
        clearTimeout(timer);
        try { pc.close(); } catch (er) { void er; }
        resolve({ ok: false, detail: e?.message || "createOffer failed" });
      }
    });
  });
}

/** Probe — Web Audio API: we create an AudioContext + start it, then close.
 *  Verifies the browser's audio engine is available + not blocked. */
async function probeAudioContext() {
  return timed(async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return { ok: false, detail: "AudioContext not supported" };
    let ctx;
    try { ctx = new AC(); } catch (e) { return { ok: false, detail: e?.message || "ctor failed" }; }
    const ok = ctx.state !== "closed";
    const detail = `state=${ctx.state} · sampleRate=${ctx.sampleRate}Hz`;
    try { await ctx.close(); } catch (e) { void e; }
    return { ok, detail };
  });
}

/** Probe — Yjs library is loaded and a doc can be created. */
async function probeYjs() {
  return timed(async () => {
    try {
      const Y = await import("yjs");
      const doc = new Y.Doc();
      doc.getMap("probe").set("k", 1);
      const update = Y.encodeStateAsUpdate(doc);
      doc.destroy();
      return { ok: update.length > 0, detail: `${update.length} bytes for one map insert` };
    } catch (e) {
      return { ok: false, detail: e?.message || "import failed" };
    }
  });
}

/** Probe — getUserMedia (no actual permission grab; just feature detection +
 *  enumeration of devices that are visible without permission). */
async function probeMedia() {
  return timed(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      return { ok: false, detail: "MediaDevices API unavailable" };
    }
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const mics = list.filter((d) => d.kind === "audioinput").length;
      return { ok: mics > 0, detail: `${mics} audio input device(s) visible` };
    } catch (e) {
      return { ok: false, detail: e?.message || "enumerateDevices failed" };
    }
  });
}

function Dot({ ok, loading }) {
  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-white/40" />;
  if (ok)      return <CheckCircle2 className="h-4 w-4 text-emerald-300" />;
  return <XCircle className="h-4 w-4 text-rose-300" />;
}

function Card({ icon: Icon, title, result, loading, testid, highlighted }) {
  const ok = result?.ok;
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-4 backdrop-blur transition ${
        loading
          ? "border-white/10 bg-white/[0.025]"
          : ok
          ? "border-emerald-400/30 bg-emerald-500/[0.08]"
          : "border-rose-400/30 bg-rose-500/[0.06]"
      } ${
        highlighted
          ? "ring-2 ring-fuchsia-400/70 shadow-[0_0_24px_rgba(217,70,239,0.45)]"
          : ""
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-white/70" />
          <p className="font-display text-[14px] font-semibold text-white">{title}</p>
        </div>
        <Dot ok={ok} loading={loading} />
      </div>
      <p className="font-mono text-[11px] leading-5 text-white/65">
        {loading ? "Probing…" : (result?.detail || (ok ? "OK" : "Failed"))}
      </p>
      {result?.ms != null ? (
        <p className="mt-1 font-mono text-[9.5px] uppercase tracking-wider text-white/35">
          {result.ms} ms
        </p>
      ) : null}
    </div>
  );
}

export default function StatusPage() {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(true);
  // "Send to support" form state — local-only; we never POST this anywhere,
  // we just build a sanitized JSON blob the user can paste into a support
  // ticket / chat. Keeps the page zero-extra-infra.
  const [bugDesc, setBugDesc] = useState("");
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  // Console tail — refreshed every 2s while the page is mounted so newly
  // captured lines appear without a manual re-probe. Read from the
  // module singleton in `@/lib/consoleTail` which was installed at app boot.
  const [consoleTail, setConsoleTail] = useState(() => getConsoleTail());
  useEffect(() => {
    const id = window.setInterval(() => setConsoleTail(getConsoleTail()), 2000);
    return () => window.clearInterval(id);
  }, []);
  // Deep-link target probe — set via `?probe=<key>` on the URL so an error
  // toast elsewhere in the app can link directly to /status with the exact
  // failing probe highlighted. Cleared once consumed so a refresh doesn't
  // re-trigger the highlight.
  const [highlightedProbe, setHighlightedProbe] = useState(null);
  const supportPanelRef = useRef(null);

  // Parse `?desc=...&probe=...` once on mount. Both params are optional.
  // - `desc`: prefills the support textarea (URL-decoded). Cap at 500 chars
  //   to match the textarea's maxLength so we never exceed the field.
  // - `probe`: scrolls the support panel into view + ring-highlights the
  //   matching probe card. Valid values are the probe keys defined in the
  //   `probes` memo below; an unknown value is silently dropped.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const desc = url.searchParams.get("desc");
      const probe = url.searchParams.get("probe");
      if (desc) setBugDesc(desc.slice(0, 500));
      if (probe) {
        setHighlightedProbe(probe);
        // Auto-dim the highlight after 6s so it doesn't linger if the user
        // ignores it. We keep the prefilled desc; only the visual cue fades.
        window.setTimeout(() => setHighlightedProbe(null), 6000);
      }
      if (desc || probe) {
        // Scrub the params so a refresh doesn't re-fire the highlight +
        // a bookmark of the cleaned URL still loads fine.
        url.searchParams.delete("desc");
        url.searchParams.delete("probe");
        window.history.replaceState({}, "", url.toString());
        // Defer the scroll so the support panel has rendered.
        window.setTimeout(() => {
          supportPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 600);
      }
    } catch (e) { void e; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const probes = useMemo(() => ([
    { key: "backend",   title: "Backend API + Mongo",     icon: Database, run: probeBackend },
    { key: "websocket", title: "WebSocket (collab)",      icon: Wifi,     run: probeWebSocket },
    { key: "stun",      title: "STUN reachability",       icon: Network,  run: probeSTUN },
    { key: "audio",     title: "Web Audio API",           icon: Headphones, run: probeAudioContext },
    { key: "yjs",       title: "Yjs CRDT library",        icon: Cpu,      run: probeYjs },
    { key: "media",     title: "Media devices",           icon: Mic,      run: probeMedia },
  ]), []);

  const runAll = useCallback(async () => {
    setRunning(true);
    // Run probes in parallel — each is independent and has its own timeout.
    const entries = await Promise.all(
      probes.map(async (p) => [p.key, await p.run()])
    );
    setResults(Object.fromEntries(entries));
    setRunning(false);
  }, [probes]);

  // Initial run + auto-refresh every 10 s. Cleared on unmount.
  useEffect(() => {
    runAll();
    const id = window.setInterval(runAll, 10000);
    return () => window.clearInterval(id);
  }, [runAll]);

  const okCount = Object.values(results).filter((r) => r?.ok).length;
  const totalCount = probes.length;
  const allGreen = !running && okCount === totalCount;
  const backendResult = results.backend;
  const integrations = backendResult?.extra?.integrations;

  /** Build the sanitized JSON support report.
   *
   *  Includes:
   *   - per-probe results (ok/detail/ms)
   *   - browser fingerprint a support agent actually needs
   *     (UA, language, platform hint, viewport size, online flag)
   *   - the user's bug description (verbatim — they wrote it themselves)
   *   - app URL + backend host (NEVER any token / key — we read from process.env
   *     only public REACT_APP_BACKEND_URL is exposed at all)
   *
   *  Strictly excludes:
   *   - localStorage / sessionStorage (could contain ADMIN_TOKEN, user names)
   *   - cookies / IndexedDB
   *   - any integration key values (we already enforced boolean-only at the
   *     backend, but we re-check here defensively)
   */
  const buildReport = useCallback(() => {
    const safeIntegrations = {};
    if (integrations) {
      Object.entries(integrations).forEach(([k, v]) => {
        // Force booleans even if the backend ever regresses to leak strings.
        safeIntegrations[k] = v === true;
      });
    }
    return {
      app: "ELPM Studio Pro",
      version: "iter-42",
      timestamp: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href.split("?")[0] : "",
      bug_description: bugDesc.trim() || "(none provided)",
      browser: {
        userAgent: navigator?.userAgent || "",
        language: navigator?.language || "",
        platform: navigator?.platform || "",
        online: navigator?.onLine !== false,
        cookieEnabled: !!navigator?.cookieEnabled,
        viewport: { w: window?.innerWidth || 0, h: window?.innerHeight || 0 },
        devicePixelRatio: window?.devicePixelRatio || 1,
      },
      probes: probes.map((p) => ({
        key: p.key,
        title: p.title,
        ok: !!results[p.key]?.ok,
        ms: results[p.key]?.ms ?? null,
        detail: results[p.key]?.detail ?? null,
      })),
      backend: {
        api: API,
        mongo_ok: backendResult?.extra?.mongo_ok ?? null,
        active_rooms: backendResult?.extra?.collab?.active_rooms ?? null,
        active_clients: backendResult?.extra?.collab?.active_clients ?? null,
        integrations: safeIntegrations,
      },
      // Last 20 console.error / console.warn lines captured by the
      // boot-time interceptor. Already length-capped per arg so the
      // report stays small enough to fit in a chat message.
      console_tail: consoleTail.map((e) => ({
        level: e.level,
        text: e.text,
        ts: e.ts,
      })),
    };
  }, [bugDesc, probes, results, integrations, backendResult, consoleTail]);

  const handleCopyReport = useCallback(async () => {
    const report = buildReport();
    const text = JSON.stringify(report, null, 2);
    /** Try the modern Clipboard API; if it's missing OR throws a permission
     *  error (common in CI/headless and restrictive browsers), fall back to
     *  the synchronous execCommand path which works without permission
     *  inside a user-gesture handler. Returns true on either path's success. */
    const writeToClipboard = async () => {
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) { void e; /* fall through to execCommand */ }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        ta.style.top = "0";
        ta.style.left = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        return ok;
      } catch (e) { void e; return false; }
    };
    const ok = await writeToClipboard();
    if (ok) {
      setCopied(true);
      toast.success("Support report copied", {
        description: "Paste into your support ticket or chat. No secrets included.",
      });
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error("Copy failed", {
        description: "Your browser blocked clipboard access. Select all in a console + copy manually.",
      });
    }
  }, [buildReport]);

  // Clear the copy-state timer on unmount.
  useEffect(() => () => window.clearTimeout(copyTimerRef.current), []);

  /** Download the support report as a timestamped `.json` file. Pure
   *  client-side Blob + anchor click — no network round-trip. The filename
   *  uses the local-time slug so a user collecting reports over a session
   *  can sort them in their downloads folder chronologically. */
  const handleDownloadReport = useCallback(() => {
    const report = buildReport();
    const text = JSON.stringify(report, null, 2);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const slug = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-` +
                 `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = `elpm-diag-${slug}.json`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Diagnostic file downloaded", {
        description: `elpm-diag-${slug}.json saved — attach it to your support ticket.`,
      });
    } catch (e) {
      toast.error("Download failed", { description: e?.message || "Browser blocked the download" });
    } finally {
      // Release the object URL on next tick — the click handler is sync.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }, [buildReport]);

  /** Open a `mailto:` deep-link prefilled with a SUMMARY of the report
   *  (mailto URLs have a ~2 kB practical length limit across major mail
   *  clients, so we can't stuff the full JSON; users should attach the
   *  downloaded .json from `handleDownloadReport`).
   *
   *  Body contains: user description + probe pass/fail counts + browser
   *  one-liner. Subject is auto-tagged so support inbox rules can route. */
  const handleEmailReport = useCallback(() => {
    const report = buildReport();
    const passing = report.probes.filter((p) => p.ok).length;
    const failing = report.probes.filter((p) => !p.ok);
    const subject = `[ELPM Studio] Support — ${passing}/${report.probes.length} probes green`;
    const lines = [
      "Hi — using the in-app support tool to send this:",
      "",
      `User description: ${report.bug_description}`,
      "",
      `App: ${report.app} (${report.version})`,
      `Time: ${report.timestamp}`,
      `URL: ${report.url}`,
      "",
      "Probes:",
      ...report.probes.map((p) => `  ${p.ok ? "OK" : "FAIL"}  ${p.title}${p.detail ? " — " + p.detail.slice(0, 80) : ""}${p.ms != null ? " (" + p.ms + " ms)" : ""}`),
      "",
      `Browser: ${report.browser.userAgent}`,
      `Viewport: ${report.browser.viewport.w}×${report.browser.viewport.h} · DPR ${report.browser.devicePixelRatio} · ${report.browser.language}`,
      "",
      "(Full JSON diagnostic — including console tail — attached as a downloaded .json file.)",
      ...(failing.length ? ["", "Failures:", ...failing.map((f) => `  • ${f.title}: ${f.detail || ""}`.slice(0, 200))] : []),
    ].join("\n");
    // Length-cap defensively at ~1800 chars so even strict mail clients open.
    const safeBody = lines.length > 1800 ? lines.slice(0, 1800) + "\n…(truncated — see attached .json)" : lines;
    const href = `mailto:${encodeURIComponent(SUPPORT_EMAIL)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(safeBody)}`;
    try {
      window.location.href = href;
      toast.message("Opening your mail app", {
        description: "Attach the downloaded JSON for the full diagnostic.",
      });
    } catch (e) {
      toast.error("Mail link failed", { description: e?.message || "Unable to open mail client" });
    }
  }, [buildReport]);

  return (
    <div className="min-h-screen overflow-x-hidden font-body text-white" data-testid="status-page">
      <title>Studio Status · ELPM Studio Pro</title>
      <AmbientBackdrop />

      <div className="relative z-10 mx-auto max-w-5xl p-4 md:p-8">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="status-home-link"
            className="group flex items-center gap-3 transition hover:opacity-90"
          >
            <img
              src="/brand/elpm-logo.png"
              alt="ELPM Studio Pro"
              data-testid="brand-wordmark-status"
              className="h-9 w-auto select-none drop-shadow-[0_2px_14px_rgba(56,189,248,0.2)]"
              draggable="false"
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition group-hover:text-sky-200">
              <ArrowLeft className="inline h-3 w-3" /> back to studio
            </span>
          </Link>
          <button
            type="button"
            data-testid="status-refresh-btn"
            onClick={runAll}
            disabled={running}
            className="studio-pill rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/65 transition hover:border-white/20 hover:bg-white/[0.08] disabled:opacity-50"
          >
            {running ? (
              <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 inline h-3.5 w-3.5" />
            )}
            Re-probe
          </button>
        </header>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card mb-6 rounded-3xl p-6"
          data-testid="status-summary"
        >
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-sky-300">
            <Activity className="h-3.5 w-3.5" />Self-test · Live
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
            Studio Status
          </h1>
          <p className="mt-2 max-w-2xl font-body text-[14px] leading-6 text-white/65">
            Real client-side probes against every primitive the collab stack
            depends on. If you're reporting an issue, screenshot this page —
            it tells support most of what they need to know.
          </p>
          <div
            className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-wider ${
              running ? "bg-white/10 text-white/65"
              : allGreen ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40"
              : "bg-amber-500/20 text-amber-100 ring-1 ring-amber-400/40"
            }`}
            data-testid="status-summary-pill"
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> :
             allGreen ? <CheckCircle2 className="h-3 w-3" /> :
             <AlertTriangle className="h-3 w-3" />}
            {running ? "Running probes…" : `${okCount} of ${totalCount} probes green`}
          </div>
        </motion.section>

        <section className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="status-grid">
          {probes.map((p) => (
            <Card
              key={p.key}
              testid={`status-card-${p.key}`}
              icon={p.icon}
              title={p.title}
              loading={running}
              result={results[p.key]}
              highlighted={highlightedProbe === p.key}
            />
          ))}
        </section>

        {integrations ? (
          <section
            data-testid="status-integrations"
            className="mb-6 rounded-3xl border border-white/[0.07] bg-white/[0.025] p-6"
          >
            <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
              <KeyRound className="h-3.5 w-3.5" />Integration · Keys
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {Object.entries(integrations).map(([k, ok]) => (
                <div
                  key={k}
                  data-testid={`status-integration-${k}`}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 ring-1 ${
                    ok ? "bg-emerald-500/15 text-emerald-100 ring-emerald-400/30"
                    : "bg-rose-500/10 text-rose-100 ring-rose-400/25"
                  }`}
                >
                  {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  <span className="font-mono text-[10.5px] uppercase tracking-wider">{k}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* Console Tail — rolling buffer of console.error / console.warn /
            window.error / unhandledrejection captured for the lifetime of
            the tab. Most error reports never made it into devtools because
            the user didn't have it open; this surfaces them automatically. */}
        <section
          data-testid="status-console-tail"
          className="mb-6 rounded-3xl border border-white/[0.07] bg-black/30 p-6"
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
              <Terminal className="h-3.5 w-3.5" />Console · Tail (last {consoleTail.length})
            </div>
            <button
              type="button"
              data-testid="status-console-clear-btn"
              onClick={() => { clearConsoleTail(); setConsoleTail([]); toast.message("Console tail cleared"); }}
              disabled={consoleTail.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-wider text-white/55 transition hover:bg-white/[0.1] disabled:opacity-40"
            >
              <Eraser className="h-3 w-3" />Clear
            </button>
          </div>
          {consoleTail.length === 0 ? (
            <p
              data-testid="status-console-empty"
              className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-3 text-center font-mono text-[11px] text-white/45"
            >
              No warnings or errors captured this session — clean run.
            </p>
          ) : (
            <ul
              data-testid="status-console-list"
              className="max-h-64 space-y-1 overflow-auto rounded-xl border border-white/[0.05] bg-black/40 p-3 font-mono text-[10.5px] leading-5"
            >
              {consoleTail.map((e, idx) => (
                <li
                  key={`tail-${idx}-${e.ts}`}
                  data-testid={`status-console-line-${e.level}`}
                  className={`flex items-start gap-2 rounded px-1.5 py-0.5 ${
                    e.level === "error" ? "text-rose-200" : "text-amber-200"
                  }`}
                >
                  <span className="flex-shrink-0 font-semibold uppercase tracking-wider">
                    {e.level === "error" ? "ERR" : "WRN"}
                  </span>
                  <span className="break-all">{e.text}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* "Send to support" — pure client-side clipboard write. The report
            is a structured JSON of probe results + browser context + the
            user's bug description. No secrets, no localStorage, no cookies. */}
        <section
          ref={supportPanelRef}
          data-testid="status-support-panel"
          className="mb-6 rounded-3xl border border-fuchsia-400/20 bg-fuchsia-500/[0.05] p-6"
        >
          <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-fuchsia-300">
            <LifeBuoy className="h-3.5 w-3.5" />Send · To · Support
          </div>
          <p className="mb-3 max-w-2xl font-body text-[13.5px] leading-6 text-white/70">
            Stuck? Describe what you saw, then click <em>Copy support report</em>.
            We'll build a sanitized JSON blob (probes + browser fingerprint +
            recent console output, no secrets) and copy it to your clipboard.
            Paste it into a support ticket or chat and we'll have everything
            we need to triage.
          </p>
          <textarea
            data-testid="status-support-desc"
            value={bugDesc}
            onChange={(e) => setBugDesc(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="What happened? (e.g. 'My collab room shows 0 peers even after sharing the link with a friend')"
            className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 font-body text-[13px] text-white outline-none transition focus:border-fuchsia-400/60 focus:ring-2 focus:ring-fuchsia-400/30"
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-[9.5px] uppercase tracking-wider text-white/40">
              {bugDesc.trim().length} / 500 chars · nothing is sent until you click
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="status-support-download-btn"
                onClick={handleDownloadReport}
                title="Download as a timestamped .json file"
                className="inline-flex items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/80 transition hover:border-white/20 hover:bg-white/[0.1]"
              >
                <Download className="h-3.5 w-3.5" />
                Download .json
              </button>
              <button
                type="button"
                data-testid="status-support-email-btn"
                onClick={handleEmailReport}
                title={SUPPORT_EMAIL ? `Opens your mail client (to: ${SUPPORT_EMAIL})` : "Opens your mail client"}
                className="inline-flex items-center gap-1.5 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/80 transition hover:border-white/20 hover:bg-white/[0.1]"
              >
                <Mail className="h-3.5 w-3.5" />
                Email
              </button>
              <button
                type="button"
                data-testid="status-support-copy-btn"
                onClick={handleCopyReport}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-semibold text-white shadow-[0_12px_36px_-12px_rgba(217,70,239,0.55)] transition ${
                  copied
                    ? "bg-emerald-500/85 hover:bg-emerald-500"
                    : "bg-gradient-to-r from-fuchsia-500/85 to-sky-500/85 hover:from-fuchsia-500 hover:to-sky-500"
                }`}
              >
                {copied ? (
                  <><CheckCircle2 className="h-4 w-4" />Copied — paste into your ticket</>
                ) : (
                  <><Send className="h-4 w-4" />Copy support report</>
                )}
              </button>
            </div>
          </div>
        </section>

        <footer className="mt-10 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-white/45">
          <span className="flex items-center gap-2">
            <Radio className="h-3.5 w-3.5" />
            Auto re-probes every 10s
          </span>
          <Link to="/" className="hover:text-white/70">Open studio →</Link>
        </footer>
      </div>
    </div>
  );
}
